import React, { useState, useMemo, useEffect } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn, TableAction } from "@shared/types/table";
import { MapPin, Clock, FilterX, DollarSign } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useCreditNoteExport,
  useCreditNotes,
  usePaymentMethod,
  usePaymentReceivedStatusChoices,
} from "@features/cx/payments/hooks";
import { CreditNote, CreditNoteFilters } from "@features/cx/payments/types";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { CreditNoteSummary } from "../creditnotesummary";
import { CreditNoteDetailDialog } from "../creditnotedetails/creditnotedetails";
import { Button } from "@shared/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { PAGE_SIZE } from "@shared/utils/constants";

const statusBadge = (status: string) => {
  if (status === "Active")
    return (
      <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-medium">
        Active
      </span>
    );
  if (status === "Inactive")
    return (
      <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-medium">
        Inactive
      </span>
    );
  if (status === "Pending")
    return (
      <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-medium">
        Pending
      </span>
    );
  if (status === "Disconnected")
    return (
      <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-medium">
        Disconnected
      </span>
    );
  return (
    <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-medium">
      {status}
    </span>
  );
};

const paymentBadge = (status: string) => {
  if (status === "Paid")
    return (
      <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-medium">
        Paid
      </span>
    );
  if (status === "Overdue")
    return (
      <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-medium">
        Overdue
      </span>
    );
  if (status === "Pending")
    return (
      <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-medium">
        Pending
      </span>
    );
  return (
    <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-medium">
      {status}
    </span>
  );
};

const columns: TableColumn<CreditNote>[] = [
  {
    key: "transactionId",
    header: "Credit Note",
    type: "custom",
  },
  {
    key: "name",
    header: "Consumer",
    type: "custom",
    render: (_: any, row: CreditNote) => (
      <div>
        <div className="font-semibold">
          {row.consumerFirstName + " " + row.consumerLastName || "N/A"}
        </div>
        <div className="text-xs text-muted-foreground">
          {row.consumerNo || "N/A"}
        </div>
      </div>
    ),
  },
  {
    key: "amount",
    header: "Amount",
    type: "custom",
  },
  {
    key: "creditNoteReason",
    header: "Reason",
    type: "custom",
  },
  {
    key: "paymentReceivedStatus",
    header: "Status",
    type: "custom",
    render: (value: string) => statusBadge(value),
  },
  {
    key: "isPaymentConsiled",
    header: "Payment Consiled",
    type: "custom",
    render: (value: boolean) => {
      if (value === true)
        return (
          <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-medium">
            Applied
          </span>
        );
      return (
        <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-medium">
          Active
        </span>
      );
    },
  },
  {
    key: "paymentDate",
    header: "Created Date",
    type: "custom",
  },
  {
    key: "referenceReceiptNo",
    header: "Linked Payment",
    type: "custom",
  },
];

export const CreditNoteList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const remoteUtilityId = useRemoteUtilityId();
  const [isCreditNoteDetailOpen, setIsCreditNoteDetailOpen] = useState(false);
  const [selectedCreditNote, setSelectedCreditNote] =
    useState<CreditNote | null>(null);

  const paymentMethodQuery = usePaymentMethod({
    remoteUtilityId: Number(remoteUtilityId) || 0,
  });

  const paymentReceivedStatusChoicesQuery = usePaymentReceivedStatusChoices(
    Number(remoteUtilityId) || 0
  );

  // URL Params — single source of truth
  const currentPage = Number(searchParams.get("page") || "1");
  const currentSearch = decodeURIComponent(searchParams.get("search") || "");
  const currentStartDate = searchParams.get("startDate") || "";
  const currentEndDate = searchParams.get("endDate") || "";
  const currentPaymentMethod = searchParams.get("paymentMode") || "all";
  const currentPaymentReceivedStatus =
    searchParams.get("paymentReceivedStatus") || "all";
  const currentPaymentPayType = searchParams.get("paymentPayType") || 3;

  // UI State for pending edits
  const [pendingFilters, setPendingFilters] = useState({
    startDate: currentStartDate,
    endDate: currentEndDate,
    paymentMethod: decodeURIComponent(currentPaymentMethod),
    paymentReceivedStatus: decodeURIComponent(currentPaymentReceivedStatus),
    paymentPayType: currentPaymentPayType,
  });

  // Sync pendingFilters when URL changes
  useEffect(() => {
    setPendingFilters({
      startDate: currentStartDate,
      endDate: currentEndDate,
      paymentMethod: decodeURIComponent(currentPaymentMethod),
      paymentReceivedStatus: decodeURIComponent(currentPaymentReceivedStatus),
      paymentPayType: currentPaymentPayType,
    });
  }, [
    currentStartDate,
    currentEndDate,
    currentPaymentMethod,
    currentPaymentReceivedStatus,
    currentPaymentPayType,
  ]);

  const queryParams = useMemo(
    () => ({
      page: currentPage,
      searchData: currentSearch,
      startDate: currentStartDate,
      endDate: currentEndDate,
      paymentMode: currentPaymentMethod,
      paymentReceivedStatus: currentPaymentReceivedStatus,
      paymentPayType: Number(currentPaymentPayType),
    }),
    [
      currentPage,
      currentSearch,
      currentStartDate,
      currentEndDate,
      currentPaymentMethod,
      currentPaymentReceivedStatus,
      currentPaymentPayType,
    ]
  );

  const filters: CreditNoteFilters = useMemo(() => {
    const baseFilters: any = {
      page: queryParams.page,
      limit: PAGE_SIZE,
      remoteUtilityId: Number(remoteUtilityId),
      searchData: queryParams.searchData,
      paymentPayType: Number(queryParams.paymentPayType),
    };

    // Add date range filter using start and end dates
    if (queryParams.startDate) {
      baseFilters.startDate = queryParams.startDate;
    }

    if (queryParams.endDate) {
      baseFilters.endDate = queryParams.endDate;
    }

    // Add payment method filter
    if (queryParams.paymentMode !== "all") {
      baseFilters.paymentMode = queryParams.paymentMode;
    }

    // Add payment received status filter
    if (queryParams.paymentReceivedStatus !== "all") {
      baseFilters.paymentReceivedStatus = queryParams.paymentReceivedStatus;
    }

    // When "all" is selected, include all payment type keys
    baseFilters.paymentPayType = [3];

    return baseFilters;
  }, [queryParams, remoteUtilityId]);

  const exportFilters: CreditNoteFilters = useMemo(() => {
    const baseFilters: any = {
      remoteUtilityId: Number(remoteUtilityId),
      searchData: queryParams.searchData,
      paymentPayType: Number(queryParams.paymentPayType),
    };

    // Add date range filter using start and end dates
    if (queryParams.startDate) {
      baseFilters.startDate = queryParams.startDate;
    }

    if (queryParams.endDate) {
      baseFilters.endDate = queryParams.endDate;
    }

    // Add payment method filter
    if (queryParams.paymentMode !== "all") {
      baseFilters.paymentMode = queryParams.paymentMode;
    }

    // Add payment received status filter
    if (queryParams.paymentReceivedStatus !== "all") {
      baseFilters.paymentReceivedStatus = queryParams.paymentReceivedStatus;
    }

    // When "all" is selected, include all payment type keys
    baseFilters.paymentPayType = [3];

    return baseFilters;
  }, [queryParams, remoteUtilityId]);

  const { data } = useCreditNotes(filters);
  const { exportCSV, isExporting } = useCreditNoteExport(exportFilters);

  const handlePendingFilterChange = (
    key: keyof typeof pendingFilters,
    value: string
  ) => {
    setPendingFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleApplyFilters = () => {
    console.log("pendingFilters", pendingFilters);
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      ...(pendingFilters.startDate && { startDate: pendingFilters.startDate }),
      ...(pendingFilters.endDate && { endDate: pendingFilters.endDate }),
      ...(pendingFilters.paymentMethod && {
        paymentMode: pendingFilters.paymentMethod,
      }),
      ...(pendingFilters.paymentReceivedStatus && {
        paymentReceivedStatus: pendingFilters.paymentReceivedStatus,
      }),
      ...(pendingFilters.paymentPayType && {
        paymentPayType: pendingFilters.paymentPayType,
      }),
      page: 1,
    };

    setSearchParams(newParams);
  };

  const handleResetFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    delete newParams.startDate;
    delete newParams.endDate;
    delete newParams.paymentMode;
    delete newParams.paymentReceivedStatus;
    delete newParams.search;
    newParams.page = 1;

    setSearchParams(newParams);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      ...(currentStartDate && { startDate: currentStartDate }),
      ...(currentEndDate && { endDate: currentEndDate }),
      ...(currentPaymentMethod && { paymentMode: currentPaymentMethod }),
      ...(currentPaymentReceivedStatus && {
        paymentReceivedStatus: currentPaymentReceivedStatus,
      }),
    };
    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search;
    }
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };

    setSearchParams(newParams);
  };

  const actions: TableAction<any>[] = [
    {
      label: "View Details",
      onClick: (row: any) => {
        setSelectedCreditNote(row);
        setIsCreditNoteDetailOpen(true);
      },
    },
  ];

  const pagination = data
    ? {
        currentPage,
        totalPages:
          data.total_pages || Math.ceil((data.count || 0) / PAGE_SIZE),
        totalItems: data.count || 0,
        startItem: (currentPage - 1) * PAGE_SIZE + 1,
        endItem: Math.min(currentPage * PAGE_SIZE, data.count || 0),
        itemsPerPage: PAGE_SIZE,
      }
    : undefined;

  const FilterPanel = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Start Date</label>
          <input
            type="date"
            value={pendingFilters.startDate}
            onChange={(e) =>
              handlePendingFilterChange("startDate", e.target.value)
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Select start date"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">End Date</label>
          <input
            type="date"
            value={pendingFilters.endDate}
            onChange={(e) =>
              handlePendingFilterChange("endDate", e.target.value)
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Select end date"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            Payment Method
          </label>
          <Select
            value={pendingFilters.paymentMethod}
            onValueChange={(value) =>
              handlePendingFilterChange("paymentMethod", value)
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select payment method" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Methods</SelectItem>
              {paymentMethodQuery.data?.result.map((method) => (
                <SelectItem key={method.name} value={method.code}>
                  {method.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Payment Status
          </label>
          <Select
            value={pendingFilters.paymentReceivedStatus}
            onValueChange={(value) =>
              handlePendingFilterChange("paymentReceivedStatus", value)
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select payment status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {paymentReceivedStatusChoicesQuery.data?.result.map((status) => (
                <SelectItem key={status.key} value={String(status.key)}>
                  {status.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <Button variant="default" onClick={handleApplyFilters}>
          Apply Filters
        </Button>
        <Button variant="ghost" onClick={handleResetFilters}>
          <FilterX className="w-4 h-4 mr-1" /> Clear
        </Button>
      </div>
    </div>
  );

  const hasActiveFilters =
    currentStartDate ||
    currentEndDate ||
    currentPaymentMethod !== "all" ||
    currentPaymentReceivedStatus !== "all";

  const handleExport = () => {
    exportCSV();
  };

  return (
    <div className="space-y-6">
      <GenericTable
        data={data?.results || []}
        columns={columns}
        rowKey="id"
        searchPlaceholder="Search by name or ID..."
        searchParamName="query"
        searchDebounceMs={300}
        onSearch={handleSearch}
        pagination={pagination}
        onPageChange={handlePageChange}
        actions={actions}
        actionsType="dropdown"
        onExport={handleExport} // Add this line
        exportLoading={isExporting} // Add this line
        filterPanel={FilterPanel}
        emptyMessage="No credit notes found"
        emptyIcon={
          <DollarSign className="h-8 w-8 text-muted-foreground mb-2" />
        }
        filters={
          hasActiveFilters ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Filters active
              </span>
            </div>
          ) : null
        }
        selectable={false}
      />
      <CreditNoteDetailDialog
        open={isCreditNoteDetailOpen}
        onClose={() => setIsCreditNoteDetailOpen(false)}
        creditNote={selectedCreditNote}
      />
    </div>
  );
};
